import { useEffect, useRef, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

const isValidString = (value: unknown): value is string => {
    return typeof value === "string" && value.length > 0;
};

const getTabIndex = <T extends Record<string, number> | Record<number, string>>(
    tabEnum: T,
    selectedPageTab: string,
    defaultTab: number,
): number => {
    if (selectedPageTab in tabEnum) {
        const tabValue = tabEnum[selectedPageTab as keyof T];
        if (typeof tabValue === "number") {
            return tabValue;
        }
    }

    return defaultTab;
};

export const useSelectedPageTab = <T extends Record<string, number> | Record<number, string>>(
    tabEnum: T,
    defaultTab: number = 0,
) => {
    const { params } = useCurrentStateAndParams();
    const selectedPageTab = isValidString(params.selectedPageTab) ? params.selectedPageTab : undefined;

    const tabEnumRef = useRef(tabEnum);
    const defaultTabRef = useRef(defaultTab);
    tabEnumRef.current = tabEnum;
    defaultTabRef.current = defaultTab;

    const [selectedPageTabIndex, setSelectedPageTabIndex] = useState<number>(() => {
        if (!selectedPageTab) {
            return defaultTab;
        }
        return getTabIndex(tabEnum, selectedPageTab, defaultTab);
    });

    useEffect(() => {
        if (!selectedPageTab) {
            setSelectedPageTabIndex(defaultTabRef.current);
            return;
        }
        const tabIndex = getTabIndex(tabEnumRef.current, selectedPageTab, defaultTabRef.current);
        setSelectedPageTabIndex(tabIndex);
    }, [selectedPageTab]);

    return { selectedPageTabIndex, setSelectedPageTabIndex };
};
